import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import { CheckCircle, HelpCircle } from 'lucide-react'

const tiers = [
  {
    name: 'Starter',
    description: 'For small practices just getting started',
    priceMonthly: 'Contact',
    features: [
      'Up to 25 active clients',
      'Up to 5 staff users',
      'Core clinical documentation',
      'Authorization tracking',
      'Basic billing/claims',
      'Email support',
    ],
    cta: 'Contact Sales',
    highlighted: false,
  },
  {
    name: 'Professional',
    description: 'For growing practices needing full compliance',
    priceMonthly: 'Contact',
    features: [
      'Up to 100 active clients',
      'Up to 20 staff users',
      'Full clinical suite',
      'AI assistance features',
      'EDI claims submission',
      'Denial management',
      'Parent portal',
      'Priority support',
    ],
    cta: 'Contact Sales',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    description: 'For large organizations with custom needs',
    priceMonthly: 'Custom',
    features: [
      'Unlimited clients',
      'Unlimited users',
      'Multi-location support',
      'Custom integrations',
      'Dedicated success manager',
      'SLA guarantees',
      'Custom reporting',
      'On-site training available',
    ],
    cta: 'Contact Sales',
    highlighted: false,
  },
]

const includedInAll = [
  'HIPAA compliant infrastructure',
  'BAA included',
  'Note locking & addendum workflow',
  'Authorization governance',
  'Audit trail & compliance logging',
  'Secure document storage',
  'Regular platform updates',
  'Data export capabilities',
]

const faqs = [
  {
    question: 'Is there a setup fee?',
    answer: 'Setup fees vary based on data migration needs and training requirements. Contact us for a detailed quote based on your specific situation.',
  },
  {
    question: 'Can I switch plans later?',
    answer: 'Yes, you can upgrade your plan as your practice grows. Our team will help ensure a smooth transition.',
  },
  {
    question: 'What is included in the BAA?',
    answer: 'Our BAA covers all PHI handling, storage, processing, and transmission within our platform, meeting HIPAA requirements for business associates.',
  },
  {
    question: 'Do you offer implementation support?',
    answer: 'Yes, all plans include implementation support. Professional and Enterprise plans include more extensive onboarding and training.',
  },
  {
    question: 'What about data migration?',
    answer: 'We offer data migration services from most major EHR systems. Contact us to discuss your specific migration needs.',
  },
]

export default function PricingPage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Pricing
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              Transparent pricing based on practice size. All plans include our
              full compliance infrastructure.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Tiers */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-lg p-8 ${
                  tier.highlighted
                    ? 'bg-primary-600 text-white ring-2 ring-primary-600'
                    : 'bg-gray-50 text-gray-900'
                }`}
              >
                <h3 className="text-lg font-semibold">{tier.name}</h3>
                <p className={`mt-2 text-sm ${tier.highlighted ? 'text-primary-100' : 'text-gray-600'}`}>
                  {tier.description}
                </p>
                <p className="mt-6">
                  <span className="text-3xl font-bold">{tier.priceMonthly}</span>
                  {tier.priceMonthly !== 'Custom' && (
                    <span className={`text-sm ${tier.highlighted ? 'text-primary-200' : 'text-gray-500'}`}>
                      /month
                    </span>
                  )}
                </p>

                <ul className="mt-8 space-y-3">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckCircle
                        className={`h-5 w-5 flex-shrink-0 ${
                          tier.highlighted ? 'text-primary-200' : 'text-green-500'
                        }`}
                      />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`mt-8 block text-center py-3 px-4 rounded-md font-medium ${
                    tier.highlighted
                      ? 'bg-white text-primary-600 hover:bg-primary-50'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Included in All Plans */}
      <div className="bg-gray-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              Included in All Plans
            </h2>
            <p className="mt-2 text-gray-600">
              Core compliance features come standard
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {includedInAll.map((feature) => (
              <div
                key={feature}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                {feature}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>

            <dl className="space-y-6">
              {faqs.map((faq) => (
                <div key={faq.question} className="border-b border-gray-200 pb-6">
                  <dt className="flex items-start gap-3">
                    <HelpCircle className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="font-medium text-gray-900">{faq.question}</span>
                  </dt>
                  <dd className="mt-2 pl-8 text-gray-600">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-600 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Ready to Get Started?
          </h2>
          <p className="mt-4 text-lg text-primary-100">
            Contact us for a personalized quote based on your practice size and needs.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn bg-white text-primary-600 hover:bg-primary-50 text-base px-6 py-3">
              Request Quote
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
